import { useEffect, useState } from 'react';
import { adminConfig } from '../../config/login/adminConfig';
import { isDeviceTrusted, trustDevice } from '../../utils/deviceTrust';
import { fetchAdminGateStatus, verifyAdminAccessCode } from './adminGateApi';

const POLL_MS = 4000;
const clientUnlockCode = String(adminConfig.unlockCode || '').trim();

export function useAdminAccessGate() {
  const [unlocked, setUnlocked] = useState(() => isDeviceTrusted());
  const [checking, setChecking] = useState(() => !isDeviceTrusted());

  useEffect(() => {
    if (isDeviceTrusted()) {
      setUnlocked(true);
      setChecking(false);
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const status = await fetchAdminGateStatus();
        if (cancelled) return;
        const required = Boolean(status?.required) || Boolean(clientUnlockCode);
        if (!required) {
          setUnlocked(true);
        }
      } catch (error) {
        // Older APIs without this route should not trap the panel behind an unusable gate.
        if (error?.status === 404 && !clientUnlockCode) {
          setUnlocked(true);
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    };

    load();
    const interval = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const unlockWithCode = async (code, { trustThisDevice = false } = {}) => {
    const next = String(code || '').trim();
    if (!next) {
      throw new Error('Enter the security code.');
    }

    try {
      await verifyAdminAccessCode(next);
    } catch (error) {
      if (!clientUnlockCode || next !== clientUnlockCode) {
        throw new Error(error?.message || 'Invalid security code. Access denied.');
      }
    }

    if (trustThisDevice) {
      trustDevice();
    }
    setUnlocked(true);
    return true;
  };

  return {
    unlocked,
    checking,
    unlockWithCode,
  };
}
